<script setup lang="ts">
import { computed, provide, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import SearchBox from '@/components/SearchBox.vue'
import QuickAdd from '@/components/todos/QuickAdd.vue'
import GroupHeader from '@/components/todos/GroupHeader.vue'
import TagFilterBar from '@/components/todos/TagFilterBar.vue'
import TodoTree from '@/components/todos/TodoTree.vue'
import { useBackButton } from '@/composables/useBackButton'
import { goBack, onBackClick } from '@/router/goBack'
import { listViewPrefs, listViewReady, saveListView } from '@/composables/useListViewPrefs'
import { useListViewQuery } from '@/composables/useListViewQuery'
import { useTags } from '@/composables/useTags'
import { useToast } from '@/composables/useToast'
import { reportError } from '@/errorReport'
import { useTodosStore } from '@/stores/todos'
import { calendarsBrowseLocation } from '@/todos/entry'
import { chipStyle, tagEditLocation } from '@/todos/tags'
import { hapticSelection, hapticSuccess } from '@/telegramWebApp'
import {
  buildListView,
  groupNodes,
  isDone,
  reorderSiblings,
  tagFromParam,
  type GroupKey,
  type SortKey,
  type StatusFilter,
} from '@/todos/listView'
import { countNodes, type TodoNode } from '@/todos/tree'
import { readExtraTags, readViewQuery, withExtraTags } from '@/todos/viewQuery'
import { TODO_TREE_CONTEXT } from '@/todos/treeState'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useTodosStore()
const toast = useToast()

const calendarId = computed(() => Number(route.params.calendarId))
// (set on the tag landing page, `/todos/:calendarId/tag/:tag`)
const activeTag = computed(() => tagFromParam(route.params.tag))
// (`?tag=…` on a tag page: more tags the todos must carry as well)
const extraTags = computed(() => readExtraTags(route.query, activeTag.value))
const filterTags = computed(() => (activeTag.value ? [activeTag.value, ...extraTags.value] : []))
const calendar = computed(() => store.calendarById(calendarId.value))
const readonly = computed(() => calendar.value?.readonly ?? false)
const todos = computed(() => store.todosOf(calendarId.value))
const loading = computed(() => store.loading[calendarId.value] ?? false)
const error = computed(() => store.errors[calendarId.value] ?? null)

const status = ref<StatusFilter>('open')
const sort = ref<SortKey>('manual')
const group = ref<GroupKey>('none')
const collapsedGroups = ref(new Set<string>())
const search = ref('')
// The URL (`?status=…&sort=…&q=…`) wins over the stored preference and follows the page, see `useListViewQuery`.
useListViewQuery({
  status,
  sort,
  search,
  router,
  active: () => route.name === 'todo-list' || route.name === 'todo-tag',
})
// The view stored in the user's settings (`useListViewPrefs`) is applied once it has loaded -- for the keys the URL does not
// say -- and changes are only reported after that, so the defaults above never overwrite a stored view.
const prefsApplied = ref(false)
void listViewReady.then(() => {
  const prefs = listViewPrefs.value
  if (prefs) {
    const fromUrl = readViewQuery(route.query)
    if (fromUrl.status === undefined) status.value = prefs.status
    if (fromUrl.sort === undefined) sort.value = prefs.sort
    group.value = prefs.group
    collapsedGroups.value = new Set(prefs.collapsed_groups)
  }
  prefsApplied.value = true
})
watch([status, sort, group, collapsedGroups], () => {
  if (!prefsApplied.value) return
  saveListView({
    status: status.value,
    sort: sort.value,
    group: group.value,
    collapsed_groups: [...collapsedGroups.value],
  })
})

const nodes = computed(() =>
  buildListView(todos.value, {
    status: status.value,
    search: search.value,
    sort: sort.value,
    tags: filterTags.value,
  }),
)
const grouped = computed(() => group.value === 'tag')
const { tags, colorFor } = useTags()
const groups = computed(() =>
  groupNodes(
    nodes.value,
    new Set(
      tags.value
        .filter((tag) => tag.ignore_for_grouping)
        .map((tag) => tag.name.toLocaleLowerCase()),
    ),
    new Set(tags.value.filter((tag) => tag.is_category).map((tag) => tag.name.toLocaleLowerCase())),
  ),
)
// (the order spans several groups while grouping, so dragging is off then, as with a search)
const sortable = computed(
  () => !readonly.value && sort.value === 'manual' && !search.value.trim() && !grouped.value,
)

function groupKey(tag: string | null): string {
  return tag === null ? '' : tag.toLocaleLowerCase()
}

function toggleGroup(tag: string | null): void {
  const next = new Set(collapsedGroups.value)
  const key = groupKey(tag)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  collapsedGroups.value = next
}

function groupCount(nodes: TodoNode[]): number {
  return countNodes(nodes, (todo) => (status.value === 'done' ? isDone(todo) : !isDone(todo)))
}
const openCount = computed(() => todos.value.filter((t) => !isDone(t)).length)
const doneCount = computed(() => todos.value.length - openCount.value)

const collapsed = ref(new Set<string>())
const subtaskParentUid = ref<string | null>(null)
const subtaskParent = computed(() =>
  subtaskParentUid.value ? store.findTodo(calendarId.value, subtaskParentUid.value) : undefined,
)

function siblingsOf(parentUid: string | null, list: TodoNode[] = nodes.value): TodoNode[] | null {
  if (parentUid === null) return list
  for (const node of list) {
    if (node.todo.uid === parentUid) return node.children
    const found = siblingsOf(parentUid, node.children)
    if (found) return found
  }
  return null
}

// The landing page of a tag (a tapped chip, the dot of a group header).
function openTagPage(tag: string): void {
  if (activeTag.value) {
    addFilterTag(tag)
    return
  }
  void router.push({ name: 'todo-tag', params: { calendarId: calendarId.value, tag } })
}

// On a tag page a tapped tag narrows the filter instead of leaving the page.
function addFilterTag(tag: string): void {
  if (filterTags.value.some((t) => t.toLocaleLowerCase() === tag.toLocaleLowerCase())) return
  void router.push({ query: withExtraTags(route.query, [...extraTags.value, tag]) })
}

function removeFilterTag(tag: string): void {
  const rest = extraTags.value.filter((t) => t.toLocaleLowerCase() !== tag.toLocaleLowerCase())
  void router.push({ query: withExtraTags(route.query, rest) })
}

// The tags of this list's todos that the filter does not use yet, alphabetically.
const availableFilterTags = computed(() => {
  const used = new Set(filterTags.value.map((t) => t.toLocaleLowerCase()))
  const byKey = new Map<string, string>()
  for (const todo of todos.value) {
    for (const tag of todo.categories) {
      if (!used.has(tag.toLocaleLowerCase()) && !byKey.has(tag.toLocaleLowerCase())) {
        byKey.set(tag.toLocaleLowerCase(), tag)
      }
    }
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
})

provide(TODO_TREE_CONTEXT, {
  calendarId,
  readonly,
  collapsed,
  toggleCollapsed(uid) {
    const next = new Set(collapsed.value)
    if (next.has(uid)) next.delete(uid)
    else next.add(uid)
    collapsed.value = next
  },
  toggleDone(uid) {
    const todo = store.findTodo(calendarId.value, uid)
    if (!todo) return
    const done = !isDone(todo)
    hapticSelection()
    store
      .setDone(calendarId.value, uid, done)
      .then((saved) => {
        if (done) hapticSuccess()
        if (done && todo.rrule && !isDone(saved)) toast.info(t('list.repeats'))
      })
      .catch((e) => {
        reportError(e, 'list.err.update')
        reportError(e, 'list.err.update')
        toast.error(e, t('list.err.update'))
      })
  },
  open(uid) {
    void router.push(`/todos/${calendarId.value}/${encodeURIComponent(uid)}`)
  },
  openTag: openTagPage,
  addSubtask(parentUid) {
    subtaskParentUid.value = parentUid
  },
  reorder(parentUid, movedUid, newIndex) {
    const siblings = (siblingsOf(parentUid) ?? []).map((n) => n.todo)
    for (const change of reorderSiblings(siblings, movedUid, newIndex)) {
      store.update(calendarId.value, change.uid, { sort_order: change.sort_order }).catch((e) => {
        reportError(e, 'list.err.reorder')
        reportError(e, 'list.err.reorder')
        toast.error(e, t('list.err.reorder'))
      })
    }
  },
})

async function add(summary: string, parentUid: string | null = null): Promise<void> {
  try {
    const siblings = todos.value.filter((t) => t.parent_uid === parentUid)
    const maxOrder = Math.max(0, ...siblings.map((t) => t.sort_order ?? 0))
    await store.create(calendarId.value, {
      summary,
      parent_uid: parentUid,
      sort_order: maxOrder + 10,
    })
    hapticSelection()
  } catch (e) {
    reportError(e, 'list.err.add')
    toast.error(e, t('list.err.add'))
  }
}

function addSubtask(summary: string): void {
  const parent = subtaskParentUid.value
  if (!parent) return
  const next = new Set(collapsed.value)
  next.delete(parent)
  collapsed.value = next
  void add(summary, parent)
}

watch(
  calendarId,
  (id, _old, onCleanup) => {
    if (!store.calendarsLoaded) void store.loadCalendars().catch(() => {})
    void store.loadTodos(id)
    onCleanup(store.watch(id))
  },
  { immediate: true },
)

useBackButton(() =>
  goBack(router, activeTag.value ? `/todos/${calendarId.value}` : calendarsBrowseLocation),
)
</script>

<template>
  <section class="list-page">
    <header class="page-header">
      <RouterLink
        :to="activeTag ? `/todos/${calendarId}` : calendarsBrowseLocation"
        class="back"
        :aria-label="activeTag ? t('list.allTodos') : t('list.allLists')"
        @click="onBackClick($event, router, activeTag ? `/todos/${calendarId}` : calendarsBrowseLocation)"
        >‹</RouterLink
      >
      <div class="title">
        <h1 v-if="activeTag" class="tag-title" :style="chipStyle(colorFor(activeTag))">
          # {{ activeTag }}
        </h1>
        <h1 v-else>{{ calendar?.name ?? t('list.fallbackTitle') }}</h1>
        <span v-if="activeTag" class="ui-hint tag-hint">{{ t('list.tagPage') }}</span>
        <span v-else class="ui-hint">
          {{ calendar?.account_name }} · {{ t('list.openCount', { count: openCount })
          }}<template v-if="doneCount"> · {{ t('list.doneCount', { count: doneCount }) }}</template>
          <template v-if="readonly"> · {{ t('calendars.readonlyChip') }}</template>
        </span>
      </div>
      <RouterLink
        v-if="activeTag"
        :to="tagEditLocation(activeTag)"
        class="ui-btn ui-btn-icon"
        :aria-label="t('list.editTag', { tag: activeTag })"
        :title="t('list.editTag', { tag: activeTag })"
        >✎</RouterLink
      >
      <button
        type="button"
        class="ui-btn ui-btn-icon"
        :disabled="loading"
        :aria-label="t('list.refresh')"
        @click="store.loadTodos(calendarId)"
      >
        {{ loading ? '…' : '⟳' }}
      </button>
    </header>

    <TagFilterBar
      v-if="activeTag"
      :tags="extraTags"
      :available="availableFilterTags"
      :color-for="colorFor"
      @add="addFilterTag"
      @remove="removeFilterTag"
    />

    <UiGroup>
      <SearchBox
        v-model="search"
        :placeholder="t('list.search')"
        :clear-label="t('list.clearSearch')"
      />
    </UiGroup>

    <UiGroup>
      <div class="controls">
        <div class="ui-segmented" role="group" :aria-label="t('list.show')">
          <button type="button" :aria-pressed="status === 'open'" @click="status = 'open'">
            {{ t('list.status.open') }}
          </button>
          <button type="button" :aria-pressed="status === 'all'" @click="status = 'all'">
            {{ t('list.status.all') }}
          </button>
          <button type="button" :aria-pressed="status === 'done'" @click="status = 'done'">
            {{ t('list.status.done') }}
          </button>
        </div>
        <select v-model="sort" class="ui-select sort" :aria-label="t('list.sortBy')">
          <option value="manual">{{ t('list.sort.manual') }}</option>
          <option value="due">{{ t('list.sort.due') }}</option>
          <option value="priority">{{ t('list.sort.priority') }}</option>
          <option value="created">{{ t('list.sort.created') }}</option>
          <option value="title">{{ t('list.sort.title') }}</option>
        </select>
        <select v-model="group" class="ui-select sort" :aria-label="t('list.groupBy')">
          <option value="none">{{ t('list.groupBy') }}: {{ t('list.group.none') }}</option>
          <option value="tag">{{ t('list.groupBy') }}: {{ t('list.group.tag') }}</option>
        </select>
      </div>
    </UiGroup>

    <p v-if="error" class="ui-error">{{ error }}</p>
    <p v-else-if="loading && !todos.length" class="ui-hint">{{ t('list.loadingServer') }}</p>
    <p v-else-if="!nodes.length" class="ui-hint empty">
      <template v-if="search">{{ t('list.noMatch', { search }) }}</template>
      <template v-else-if="extraTags.length">{{ t('list.noTagsMatch') }}</template>
      <template v-else-if="activeTag">{{ t('list.noTagMatch', { tag: activeTag }) }}</template>
      <template v-else-if="status === 'open' && doneCount">{{ t('list.allDone') }}</template>
      <template v-else>{{ t('list.noTodos') }}</template>
    </p>
    <template v-else-if="grouped">
      <UiGroup v-for="g in groups" :key="groupKey(g.tag)">
        <GroupHeader
          :name="g.tag"
          :color="g.tag ? colorFor(g.tag) : null"
          :count="groupCount(g.nodes)"
          :expanded="!collapsedGroups.has(groupKey(g.tag))"
          @toggle="toggleGroup(g.tag)"
          @open-tag="g.tag !== null && openTagPage(g.tag)"
        />
        <UiList v-if="!collapsedGroups.has(groupKey(g.tag))">
          <TodoTree :nodes="g.nodes" :parent-uid="null" :sortable="false" :depth="0" />
        </UiList>
      </UiGroup>
    </template>
    <UiGroup v-else>
      <UiList>
        <TodoTree :nodes="nodes" :parent-uid="null" :sortable="sortable" :depth="0" />
      </UiList>
    </UiGroup>
    <p v-if="nodes.length && sortable" class="ui-hint">
      {{ t('list.dragHint', { count: countNodes(nodes, () => true) }) }}
    </p>

    <div v-if="subtaskParent" class="ui-backdrop" @click.self="subtaskParentUid = null">
      <div class="ui-sheet">
        <i18n-t keypath="list.addSubtaskTo" tag="p" class="sheet-title">
          <template #title>
            <strong>{{ subtaskParent.summary }}</strong>
          </template>
        </i18n-t>
        <QuickAdd :placeholder="t('list.subtaskPlaceholder')" autofocus @add="addSubtask" />
        <button type="button" class="ui-btn done" @click="subtaskParentUid = null">
          {{ t('common.done') }}
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.back {
  font-size: 1.8rem;
  line-height: 1;
  text-decoration: none;
}

.title {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.tag-hint {
  margin-top: 0.25rem;
}

.tag-title {
  display: inline-block;
  max-width: 100%;
  padding: 0.05rem 0.6rem;
  border-radius: 999px;
}

h1 {
  margin: 0;
  font-size: 1.3rem;
  overflow-wrap: anywhere;
}

.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
}

.sort {
  width: auto;
}

.empty {
  padding: 1.5rem 0;
  text-align: center;
}

.sheet-title {
  margin: 0 0 0.6rem;
}

.done {
  width: 100%;
  margin-top: 0.6rem;
}
</style>
